<template>
  <v-container id="dashboard" fluid>
    <v-card>
      <v-system-bar color="schedule" />
      <v-dialog
        persistent
        max-width="600px"
        v-model="confirmDeleteDialog"
        @keydown.esc="confirmDeleteDialog = false"
      >
        <v-card autofocus @keydown.esc="cancel">
          <v-card-title class="text-h5">
            スケジュールの削除確認
          </v-card-title>
          <v-card-text>
            スケジュールと一緒にプレイリストも削除しますか？
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="warning" outlined @click="cancelDelete">
              キャンセル
            </v-btn>
            <v-btn color="red" outlined @click="deleteEventOnly">
              スケジュールのみ削除
            </v-btn>
            <v-btn color="blue" outlined @click="deleteEventWithPlaylist">
              プレイリストと一緒に削除
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-toolbar flat>
        <v-icon large left color="schedule">mdi-calendar</v-icon>
        <v-toolbar-title>
          {{ scheduleTitle }}
        </v-toolbar-title>
        <v-spacer />
        <v-skeleton-loader :loading="scheduleLoading" type="actions">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn
                color="schedule"
                v-on="on"
                @click="makeDraftEvent({ date: today })"
              >
                <v-icon left>mdi-light mdi-plus</v-icon>
                <span>新規作成</span>
              </v-btn>
            </template>
            <span>新規作成</span>
          </v-tooltip>
        </v-skeleton-loader>
      </v-toolbar>

      <v-skeleton-loader :loading="scheduleLoading" type="date-picker">
        <v-card
          elevation="0"
          :loading="eventsLoading"
          :disabled="!selectedSchedule || eventsLoading"
        >
          <v-toolbar flat>
            <v-toolbar-title>
              <span
                class="text-subtitle-1"
                v-text="`更新: ${scheduleLastUpdated}`"
              ></span>
            </v-toolbar-title>
            <v-btn fab text @click="prev()">
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <v-toolbar-title>
              {{ calendarTitle }}
            </v-toolbar-title>
            <v-btn fab text @click="next()">
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
            <v-spacer />
            <v-btn outlined @click="setToday">
              今日
            </v-btn>
            <v-col cols="2" md="2" sm="3">
              <v-select
                v-model="type"
                :items="types"
                dense
                outlined
                hide-details
                class="ml-6 mb-1"
              ></v-select>
            </v-col>
          </v-toolbar>
          <v-sheet height="600">
            <!-- Key binding forces calendar update when selectedSchedule has changed, or events were updated -->
            <v-calendar
              :key="
                selectedSchedule
                  ? `${selectedSchedule.id}_${this.formattedEvents.length}`
                  : 0
              "
              ref="calendar"
              v-model="focus"
              color="primary"
              :type="type"
              :events="formattedEvents"
              :event-color="event => event.color"
              :now="today"
              :weekdays="weekday"
              :interval-format="interval => interval.time"
              @click:event="showEvent"
              @click:day="makeDraftEvent"
              @change="updateRange"
              @click:date="viewDay"
              @click:more="viewDay"
            >
              <!-- label for each schedule -->
              <template v-slot:event="{ event }">
                <b>{{ getTimeArea(event) }}</b
                >: {{ getPlaylistName(event.playlist) }}:
                <i v-for="name in getDeviceNames(event)" :key="name">
                  {{ name }}
                </i>
              </template>
            </v-calendar>

            <v-menu
              v-model="selectedEventOpen"
              :close-on-content-click="false"
              :activator="selectedEventElement"
              min-width="360px"
              max-width="600px"
              class="ma-0 pa-0"
            >
              <div v-if="selectedEvent && selectedEvent.root">
                <schedule-event-draft
                  class="ma-0 pa-0"
                  :event="type !== 'month' ? selectedEvent.root : selectedEvent"
                  :removable="true"
                  @save="updateEvent"
                  @remove="confirmDeleteSchedule"
                  @cancel="clearDialogs"
                />
              </div>
            </v-menu>

            <v-dialog
              content-class="event-dialog"
              v-model="selectedDateOpen"
              max-width="720px"
              transition="slide-x-reverse-transition"
            >
              <schedule-event-card
                :event="eventDraft"
                @save="addEvent"
                @cancel="clearDialogs"
              />
            </v-dialog>
          </v-sheet>
        </v-card>
      </v-skeleton-loader>
    </v-card>
  </v-container>
</template>

<script>
import { Pick } from '@/protocol'
import { mapActions, mapGetters } from 'vuex'
import { fmt, fmtToNow } from '@/plugins/datefns'
import { differenceInDays, addDays, endOfDay, startOfDay } from 'date-fns/esm'
import ScheduleEventCard from '@/components/schedule/ScheduleEventCard'
import ScheduleEventDraft from '@/components/schedule/ScheduleEventDraft'
import { eventFunctionsMixin } from '@/plugins/mixins'
import { functions } from '@/config/firebase'
import firebase from 'firebase'
import {
  addScheduleEvent,
  updateScheduleEvent,
  deleteScheduleEvent,
  deletePlaylist
} from '@/firebaseAccess/firestoreAccess'

export default {
  components: {
    ScheduleEventCard,
    ScheduleEventDraft
  },
  mixins: [eventFunctionsMixin],
  data: () => ({
    today: fmt(new Date(), 'yyyy-MM-dd'),
    focus: fmt(new Date(), 'yyyy-MM-dd'),
    start: null,
    end: null,

    selectedSchedule: null,
    selectedScheduleEditOpen: false,
    selectedScheduleDeleteOpen: false,
    scheduleDraft: {},

    eventDraft: {},
    selectedEvent: {},
    selectedEvents: [],
    selectedEventElement: null,
    selectedEventOpen: false,
    selectedDateOpen: false,
    scheduleLoading: true,
    eventsLoading: false,

    type: 'month',
    types: [
      { text: '月', value: 'month' },
      { text: '週', value: 'week' },
      { text: '日', value: 'day' }
    ],
    weekday: [0, 1, 2, 3, 4, 5, 6],
    selectedGroup: [],

    eventToDelete: null,
    confirmDeleteDialog: false
  }),
  computed: {
    ...mapGetters({
      devices: 'devices/getDevices',
      groups: 'devices/groups/getGroups',
      playlists: 'playlists/getPlaylists',
      schedules: 'schedules/getSchedules',
      events: 'schedules/events/getScheduleEvents'
    }),
    scheduleTitle() {
      if (this.selectedSchedule) {
        if (this.selectedSchedule.name) {
          return `${this.selectedSchedule.name}`
        }
      }
      return '--'
    },
    scheduleLastUpdated() {
      if (this.selectedSchedule) {
        if (this.selectedSchedule.updated) {
          return fmtToNow(this.selectedSchedule.updated.toDate())
        }
      }
      return '--'
    },
    calendarTitle() {
      const { start, end } = this
      if (!start || !end) {
        return '--'
      }

      const startDate = new Date(start.date)
      const endDate = new Date(end.date)

      switch (this.type) {
        case 'month':
          return fmt(startDate, 'yyyy/MM')
        case 'week':
          return (
            fmt(startDate, 'yyyy/MM/dd') + ' ～ ' + fmt(endDate, 'yyyy/MM/dd')
          )
        case 'day':
          return fmt(endDate, 'yyyy/MM/dd')
      }
      return '--'
    },
    formattedEvents() {
      const timeFormattedEvents = this.events.map(e => {
        e.start = new Date(e.startDate + ' ' + e.startTime)
        e.end = new Date(e.endDate + ' ' + e.endTime)
        e.timed = false
        e.root = e
        return e
      })

      if (this.type === 'month') {
        return timeFormattedEvents
      } else {
        // To show events on timeline in day or week mode,
        // start and end date have to be changed into same day.
        // If start and end date have more than one day difference, event doesn't show up in timeline
        const devidedTimeFormattedEvents = []
        for (const event of timeFormattedEvents) {
          const devidedEvents = this.timeRangeDivider(event)
          devidedEvents.forEach(e => devidedTimeFormattedEvents.push(e))
        }

        return devidedTimeFormattedEvents
      }
    }
  },
  methods: {
    ...mapActions({
      showSnack: 'snackbar/showSnack',

      bindSchedulesRef: 'schedules/bindRef',
      bindScheduleEventsRef: 'schedules/events/bindRef',

      bindPlaylistsRef: 'playlists/bindRef',

      bindDeviceGroupsRef: 'devices/groups/bindRef',
      bindDevicesRef: 'devices/bindRef'
    }),
    viewDay({ date }) {
      this.focus = date
      this.type = 'day'
    },
    setToday() {
      this.focus = this.today
    },
    prev() {
      this.$refs.calendar.prev()
    },
    next() {
      this.$refs.calendar.next()
    },
    updateRange({ start, end }) {
      this.start = start
      this.end = end
    },
    getPlaylistName(id) {
      const playlist = this.playlists.find(p => p.id === id)
      return playlist ? playlist.name : ''
    },
    getDeviceNames(event) {
      return this.devices
        .filter(d => event.devices && event.devices.includes(d.id))
        .map(d => d.name)
    },
    makeDraftEvent({ date }) {
      this.focus = date
      const colors = this.getEventColors()
      this.eventDraft = {
        startDate: this.focus,
        endDate: this.focus,
        startTime: '07:00',
        endTime: '19:00',
        pattern: Pick.Content.Schedule.Pattern.PATTERN_DAILY,
        color: colors[this.getRandomInt(colors.length - 1)],
        nonstop: false
      }
      this.selectedDateOpen = true
    },
    getRandomInt(max) {
      return Math.floor(Math.random() * Math.floor(max))
    },
    clearDialogs() {
      this.selectedEventOpen = false
      this.selectedDateOpen = false
      this.selectedScheduleEditOpen = false
      this.selectedScheduleDeleteOpen = false
      this.eventDraft = {}
      this.scheduleDraft = {}
      this.selectedEvent = {}
    },
    showSaveSnack() {
      this.showSnack({
        message: 'スケジュールを保存しました',
        color: 'success'
      })
    },
    showDeleteSnack() {
      this.showSnack({ message: '削除されました', color: 'info' })
    },
    showErrorSnack(message) {
      this.showSnack({
        message: `保存に失敗しました: ${message}`,
        color: 'error'
      })
    },
    showEvent({ nativeEvent, event }) {
      const open = () => {
        this.selectedEvent = event
        this.selectedEventElement = nativeEvent.target
        setTimeout(() => (this.selectedEventOpen = true), 10)
      }
      if (this.selectedEventOpen) {
        this.selectedEventOpen = false
        setTimeout(open, 10)
      } else {
        open()
      }
      nativeEvent.stopPropagation()
    },
    addEvent(event) {
      addScheduleEvent(this.selectedSchedule.id, event, true)
        .then(() => {
          this.showSaveSnack()
        })
        .finally(() => {
          this.clearDialogs()
        })
    },
    async updateEvent(event) {
      this.selectedSchedule.updated = firebase.firestore.Timestamp.now()

      updateScheduleEvent(this.selectedSchedule.id, event.id, event, true)
        .then(() => {
          this.showSaveSnack()
        })
        .finally(() => {
          this.clearDialogs()
        })
    },
    async deleteEvent(event) {
      await deleteScheduleEvent(this.selectedSchedule.id, event.id, true)

      this.showDeleteSnack()
      this.clearDialogs()
    },
    timeRangeDivider(event) {
      const startDay = startOfDay(new Date(event.startDate))
      const endDay = endOfDay(new Date(event.endDate))

      const days = differenceInDays(endDay, startDay) + 1
      if (days === 1) {
        const eventDraft = {
          start: event.start,
          end: event.end,
          startDate: event.startDate,
          endDate: event.endDate,
          startTime: event.startTime,
          endTime: event.endTime,
          playlist: event.playlist,
          color: event.color,
          nonstop: event.nonstop,
          timed: true,
          root: event
        }
        return [eventDraft]
      }

      const dividedEvents = []
      for (let i = 0; i < days; i++) {
        const targetDayStr = fmt(addDays(startDay, i), 'yyyy-M-d')

        const start = new Date(`${targetDayStr} ${event.startTime}`)
        const end = new Date(`${targetDayStr} ${event.endTime}`)

        const eventDraft = {
          start,
          end,
          startDate: targetDayStr,
          endDate: targetDayStr,
          startTime: event.startTime,
          endTime: event.endTime,
          playlist: event.playlist,
          color: event.color,
          nonstop: event.nonstop,
          timed: true,
          root: event
        }

        dividedEvents.push(eventDraft)
      }
      return dividedEvents
    },
    confirmDeleteSchedule(event) {
      this.eventToDelete = event
      this.confirmDeleteDialog = true
    },
    closeConfirmDeleteDialog() {
      this.eventToDelete = null
      this.confirmDeleteDialog = false
    },
    cancelDelete() {
      this.closeConfirmDeleteDialog()
    },
    updateDevicesConfig(deviceIds) {
      const updateDevicesConfig = functions.httpsCallable(
        'device-UpdateDevicesConfig'
      )
      updateDevicesConfig({ deviceIds })
        .then(result => {
          if (result.data.success) {
            this.showSnack({
              message: 'デバイス設定の更新を保存しました',
              color: 'success'
            })
          } else {
            this.showSnack({
              message: `デバイス設定更新の保存に失敗しました: ${result.data.message}`,
              color: 'error'
            })
          }
        })
        .catch(error => {
          this.showSnack({
            message: `デバイス設定更新の保存に失敗しました: ${error}`,
            color: 'error'
          })
        })
    },
    async deleteEventOnly() {
      await this.deleteEvent(this.eventToDelete)

      // Update devices' config.
      this.updateDevicesConfig(this.eventToDelete.devices)

      this.closeConfirmDeleteDialog()
    },
    async deleteEventWithPlaylist() {
      // Check playlist of schdeule to confirm if playlist belongs to others.
      let belongToOthers = false
      const playlistId = this.eventToDelete.playlist
      for (const scheduleEvent of this.events) {
        // Skip same schedule event as the event to delete.
        if (scheduleEvent.id === this.eventToDelete.id) {
          continue
        }

        if (scheduleEvent.playlist === playlistId) {
          belongToOthers = true
        }
      }

      // If belongs to others, don't delete playlist
      if (belongToOthers) {
        this.showSnack({
          message: `他のスケジュールでも使用されているためプレイリストの削除をスキップします`,
          color: 'info'
        })
      } else {
        await deletePlaylist(playlistId)
      }

      await this.deleteEvent(this.eventToDelete)

      // Update devices' config.
      this.updateDevicesConfig(this.eventToDelete.devices)

      this.closeConfirmDeleteDialog()
    },
    getTimeArea(event) {
      return event.nonstop ? '24h' : `${event.startTime}-${event.endTime}`
    }
  },
  created() {
    const schedules = this.bindSchedulesRef()
    const devices = this.bindDevicesRef()
    const deviceGroups = this.bindDeviceGroupsRef()
    const playlists = this.bindPlaylistsRef()

    Promise.all([schedules, devices, playlists, deviceGroups, playlists]).then(
      () => {
        // take the first available schedule
        this.selectedSchedule = this.schedules[0]
        this.selectedGroup = this.groups.map(g => {
          return g.id
        })
        this.scheduleLoading = false

        const scheduleEvents = this.bindScheduleEventsRef(
          this.selectedSchedule.id
        )
        Promise.all([scheduleEvents]).then(() => {
          this.eventsLoading = false
        })
      }
    )
  }
}
</script>

<style>
.v-calendar-weekly__day-label .v-btn:hover {
  background-color: #eeeeee !important;
  transition: background-color 0.5s;
}
.v-calendar-daily_head-day-label .v-btn__content {
  color: black;
}
/* this should apply for showing datepicker, only apply this page*/
.event-dialog {
  overflow-y: visible !important;
}
</style>
